import React from 'react';
import './dash.css';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faCalendarDay,
  faChartLine,
  faCircleQuestion,
  faCommentSms,
  faEnvelope,
  faFileInvoice,
  faGear,
  faMoneyBill,
  faSchool,
  faUserCheck,
  faUserGraduate,
  faVideo
} from '@fortawesome/free-solid-svg-icons';

const documentationSections = [
  {
    id: 'overview',
    title: 'Platform Overview',
    icon: faCircleQuestion,
    intro: 'Use the dashboard as your control room. Most daily work starts with applications, interviews, invoicing, student records, and finance tracking.',
    bullets: [
      'Dashboard gives the high-level picture for applications, revenue, active students, and class activity.',
      'The left sidebar is grouped by Admissions, Finance, Academic, Marketing, Analytics, and Account tools.',
      'Use the top search and notifications to move quickly between student records and new application activity.'
    ]
  },
  {
    id: 'admissions',
    title: 'Admissions Workflow',
    icon: faUserGraduate,
    intro: 'A typical student journey starts as an inactive applicant, then moves through interview, invoicing, payment, and final admission.',
    bullets: [
      'Open `Applications` to review new applicants, then open `View Student` for the full record.',
      'Inside `View Student`, review course choice, contact details, intake, and any follow-up notes before taking action.',
      'Use `Record Admission Fee` when the first admission payment is received. The platform now saves the receipt and can share it with the student.',
      'When ready, use `Admit` in the student record to generate the official admission number and activate the student profile.',
      'If the applicant is not continuing, use `Not Joining` or `Change Course` so the record stays accurate.'
    ]
  },
  {
    id: 'interviews',
    title: 'Interview Scheduling',
    icon: faVideo,
    intro: 'Interview management is handled from the Admissions interview flow and links directly into the student portal.',
    bullets: [
      'Open `Interviews` to schedule or review interview calls for applicants who are ready for the next step.',
      'Student interview invites point to the student portal interview path, so the applicant joins from their own account.',
      'Admin interview cards show whether the host joined, the student joined, whether both joined, and how long the meeting lasted.',
      'Use interview status updates to tell the difference between scheduled, active, and completed calls.'
    ]
  },
  {
    id: 'invoicing',
    title: 'Invoices And Payments',
    icon: faFileInvoice,
    intro: 'The invoicing tools handle first invoices, fee reminders, receipts, and payment tracking for both admins and students.',
    bullets: [
      'Use `Invoicing` or `Send Invoice` when you need to prepare a first payment invoice for an applicant or student.',
      'Use `Fee Reminders` to follow up on due balances and trigger invoice reminders with current payment details.',
      'When money is received, record it from the finance income flow so the payment appears in the student fee history and generates a receipt.',
      'The shared `view-invoice` and `view-receipt` pages are now active across the admin platform and match the newer student-facing document style.'
    ]
  },
  {
    id: 'student-management',
    title: 'Student Management',
    icon: faUserCheck,
    intro: 'Once admitted, the student record becomes the main place for ongoing administrative actions.',
    bullets: [
      'Open `Active Students`, `Learning Breaks`, `Graduated Students`, `Students Graduating`, `Never Joined`, or `Other Students` depending on the student status.',
      'Inside `View Student`, you can review fee payment history, classmate context, documents, certificates, school ID access, and academic status.',
      'Use the status actions to graduate, defer, drop, expel, pause learning, or resume learning when needed.',
      'Fee payment history now includes a working `View Receipt` action for each recorded payment.'
    ]
  },
  {
    id: 'academic',
    title: 'Academic Operations',
    icon: faSchool,
    intro: 'Academic setup is managed from courses, intakes, classes, instructors, and branch records.',
    bullets: [
      'Use `Courses` to maintain course names, pricing, ranking, and duration settings.',
      'Use `Intakes` to manage available start dates and intake timing for each programme.',
      'Use `Classes` and `View Classes` to monitor class creation, delivery, and participation.',
      'Use `Instructors` and `Campuses` to keep staffing and location records up to date.'
    ]
  },
  {
    id: 'finance',
    title: 'Finance And Controls',
    icon: faMoneyBill,
    intro: 'Finance tools are separated so income, invoices, bills, expenses, and approvals can be tracked clearly.',
    bullets: [
      'Use `School Fee Income`, `Admission Fees`, and `Other Income` to capture incoming payments.',
      'Use `Invoices`, `Fee Balances`, `Fixed Bills`, `Pay Bills`, `Expenses`, `Vendors`, and `Assets` for operational finance work.',
      'Use `Requisitions` and `Requisition Approvals` to control purchase requests before spending happens.',
      'Use `Finance Reports` when you need a wider financial summary or audit trail.'
    ]
  },
  {
    id: 'communication',
    title: 'Communication Tools',
    icon: faCommentSms,
    intro: 'Communication flows are built into the admin portal so most outreach can happen from the same workspace.',
    bullets: [
      'Use `SMS` for direct phone outreach and to monitor available SMS balance.',
      'Use `Emails` for mailbox review and email communication history.',
      'Interview invites, invoices, and receipts can all generate student-facing links that open in the live portal.',
      'Use `Media Consent` when student content or marketing permissions need to be requested and tracked.'
    ]
  },
  {
    id: 'settings',
    title: 'Admin Settings And Logs',
    icon: faGear,
    intro: 'Super admins can manage platform-level configuration and review sensitive activity from the account tools.',
    bullets: [
      'Use `Profile` to confirm your admin details and review recent activity.',
      'Use `Activity Logs` to audit who opened pages or performed sensitive admin actions.',
      'Use `Settings` for platform-wide items such as configurable amounts and other system controls.',
      'Use `Admins` to review or manage admin access depending on your role.'
    ]
  },
  {
    id: 'support',
    title: 'Recommended Operating Practice',
    icon: faChartLine,
    intro: 'A few habits keep the data clean and make the platform easier for the team to trust.',
    bullets: [
      'Always record payments through the proper finance flow instead of editing balances manually.',
      'Update student status as soon as the real-world situation changes so reports and reminders stay accurate.',
      'Open the student record before taking major actions so you can confirm intake, course, payment state, and current status.',
      'Use the built-in invoice, receipt, and interview links rather than copying raw IDs into messages.'
    ]
  }
];

function HelpSupportComp() {
  return (
    <div className="helpSupportPage">
      <div className="helpSupportHero">
        <div className="helpSupportHeroCopy">
          <p className="helpSupportEyebrow">Admin documentation</p>
          <h2>How the platform works</h2>
          <p>
            This guide explains the core admin workflows across admissions, interviews,
            invoicing, student management, finance, and daily operations.
          </p>
        </div>
        <div className="helpSupportHeroPanel">
          <div>
            <span>Best place to start</span>
            <strong>Applications and View Student</strong>
          </div>
          <div>
            <span>Main flow</span>
            <strong>Apply, interview, invoice, pay, admit</strong>
          </div>
        </div>
      </div>

      <div className="helpSupportLayout">
        <aside className="helpSupportSidebar">
          <div className="helpSupportSidebarCard">
            <p className="helpSupportEyebrow">Quick links</p>
            <div className="helpSupportQuickLinks">
              {documentationSections.map((section) => (
                <a key={section.id} href={`#${section.id}`} className="helpSupportQuickLink">
                  <FontAwesomeIcon icon={section.icon} />
                  <span>{section.title}</span>
                </a>
              ))}
            </div>
          </div>

          <div className="helpSupportSidebarCard">
            <p className="helpSupportEyebrow">Common paths</p>
            <div className="helpSupportPathList">
              <div>
                <strong>Admission</strong>
                <span>`Applications` → `View Student` → `Record Admission Fee` → `Admit`</span>
              </div>
              <div>
                <strong>Interview</strong>
                <span>`Interviews` → schedule call → student joins from portal</span>
              </div>
              <div>
                <strong>Payment follow-up</strong>
                <span>`Fee Reminders` / `Income` / `Admission Fees`</span>
              </div>
            </div>
          </div>
        </aside>

        <div className="helpSupportContent">
          {documentationSections.map((section) => (
            <section key={section.id} id={section.id} className="helpSupportSectionCard">
              <div className="helpSupportSectionHeader">
                <span className="helpSupportSectionIcon">
                  <FontAwesomeIcon icon={section.icon} />
                </span>
                <div>
                  <p className="helpSupportSectionLabel">Documentation section</p>
                  <h3>{section.title}</h3>
                </div>
              </div>
              <p className="helpSupportSectionIntro">{section.intro}</p>
              <div className="helpSupportBulletList">
                {section.bullets.map((item) => (
                  <div key={item} className="helpSupportBulletItem">
                    <span className="helpSupportBulletDot" />
                    <p>{item}</p>
                  </div>
                ))}
              </div>
            </section>
          ))}

          <section className="helpSupportSectionCard helpSupportSectionCard--contact">
            <div className="helpSupportSectionHeader">
              <span className="helpSupportSectionIcon">
                <FontAwesomeIcon icon={faEnvelope} />
              </span>
              <div>
                <p className="helpSupportSectionLabel">Need something added?</p>
                <h3>Keep this documentation growing</h3>
              </div>
            </div>
            <p className="helpSupportSectionIntro">
              As new workflows are added to the platform, this page can be expanded so the team always has a
              current internal guide inside the admin portal itself.
            </p>
          </section>
        </div>
      </div>
    </div>
  );
}

export default HelpSupportComp;
